import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { GenericSearchControls } from '../components/common';
import PaginatedControls from '../components/package_manager/PaginatedControls';
import SearchQuerySummary from '../components/package_manager/SearchQuerySummary';
import { useListPageLogic } from '../hooks';
import { zoeShipmentTrackingClient } from '../services/zoe-shipment-tracking-service';

export default function ZoeShipmentTrackingPage() {
  const navigate = useNavigate();

  // Use custom hook for list page logic
  const {
    data: shipments,
    loading,
    searchValue,
    setSearchValue,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    dateFilter,
    setDateFilter,
    showAdvancedSearch,
    setShowAdvancedSearch,
    filters,
    setFilters,
    filterDraft,
    setFilterDraft,
    pagination,
    setPagination,
    hasSearchCriteria,
    handleSearch,
    handleSearchClick,
    addFilter,
    removeFilter,
    fetchAll: fetchAllShipments,
    resetAll,
    error,
    setError
  } = useListPageLogic({
    service: zoeShipmentTrackingClient,
    localStoragePrefix: 'zoeShipmentTracking',
    debounceMs: 500, // Debounce search by 500ms for all fields
    buildSearchParams: ({ searchValue, filters, dateFilter }) => {
      const filterObj = {};

      // Add advanced search filters (CarrierName, Source, etc.)
      filters.forEach((f) => {
        if (f.field && f.value) {
          filterObj[f.field] = f.value;
        }
      });

      // Add date filter if selected
      if (dateFilter !== 'default') {
        filterObj.date_filter = dateFilter;
      }

      // Only add TrackingNumber if it has a value
      if (searchValue) {
        filterObj.TrackingNumber = searchValue;
      }

      return filterObj;
    }
  });

  const formatFieldLabel = (field) => {
    switch (field) {
      case 'TrackingNumber':
        return 'Tracking Number';
      case 'CarrierName':
        return 'Carrier Name';
      case 'Source':
        return 'Source';
      default:
        return field;
    }
  };

  // Function to get status chip color based on categorized status value
  const getStatusColor = (categorizedStatus) => {
    if (!categorizedStatus) return 'default';

    switch (categorizedStatus) {
      case 'Delivered':
        return 'success';
      case 'In Transit':
        return 'info';
      case 'Checked In':
        return 'primary';
      case 'Checked Out':
        return 'secondary';
      case 'Pending':
        return 'warning';
      case 'Unclassified':
      default:
        return 'error';
    }
  };

  const filterOptions = [
    { value: 'CarrierName', label: 'Carrier Name' },
    { value: 'Source', label: 'Source' }
  ];

  const sortByList = [
    { value: 'ZoeShipmentTrackingID', label: 'Zoe Shipment Tracking ID' },
    { value: 'TrackingNumber', label: 'Tracking Number' },
    { value: 'CarrierName', label: 'Carrier Name' },
    { value: 'Source', label: 'Source' }
  ];

  return (
    <div>
      <div style={{ marginTop: 40, padding: 20 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Zoe Shipment Tracking Page
        </Typography>
        <div style={{ margin: '20px auto', width: '80%' }}>
          <GenericSearchControls
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            searchFieldLabel="Tracking Number"
            sortByList={sortByList}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            enableDateFilter={false}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            filters={filters}
            setFilters={setFilters}
            filterDraft={filterDraft}
            setFilterDraft={setFilterDraft}
            showAdvancedSearch={showAdvancedSearch}
            setShowAdvancedSearch={setShowAdvancedSearch}
            filterOptions={filterOptions}
            addFilter={addFilter}
            removeFilter={removeFilter}
            formatFieldLabel={formatFieldLabel}
            conditionalFieldInputs={[]}
            handleSearchClick={handleSearchClick}
            resetAll={resetAll}
          />

          {(searchValue || filters.length > 0 || dateFilter !== 'default') && (
            <SearchQuerySummary
              searchValue={searchValue}
              filters={filters}
              dateFilter={dateFilter}
              formatFieldLabel={formatFieldLabel}
            />
          )}

          <PaginatedControls
            pagination={pagination}
            setPagination={setPagination}
            packagesLength={shipments.length}
            searchValue={searchValue}
            filters={filters}
            handleSearch={handleSearch}
            fetchAll={fetchAllShipments}
            isSearchActive={hasSearchCriteria}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : shipments.length === 0 ? (
            <Paper sx={{ padding: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Records Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchValue || filters.length > 0
                  ? 'No shipments match your search criteria. Try adjusting your filters.'
                  : 'No shipments available.'}
              </Typography>
            </Paper>
          ) : (
            <Paper sx={{ overflow: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Tracking Number</TableCell>
                    <TableCell>Carrier Name</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shipments.map((s) => (
                    <TableRow key={s.ZoeShipmentTrackingId}>
                      <TableCell>{s.ZoeShipmentTrackingId}</TableCell>
                      <TableCell>{s.TrackingNumber}</TableCell>
                      <TableCell>{s.CarrierName}</TableCell>
                      <TableCell>{s.Source}</TableCell>
                      <TableCell>
                        <Chip
                          label={s.CategorizedStatus || s.Status || 'Unknown'}
                          color={getStatusColor(
                            s.CategorizedStatus || s.Status
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() =>
                            navigate(
                              `/zoe-shipment-tracking/${s.ZoeShipmentTrackingId}`
                            )
                          }
                        >
                          Go to Shipment Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          <PaginatedControls
            pagination={pagination}
            setPagination={setPagination}
            packagesLength={shipments.length}
            searchValue={searchValue}
            filters={filters}
            handleSearch={handleSearch}
            fetchAll={fetchAllShipments}
            isSearchActive={hasSearchCriteria}
          />
        </div>
      </div>
    </div>
  );
}
